import React, { useState } from 'react';
import './Members.css';

const Members: React.FC = () => {
  const [activeTab, setActiveTab] = useState('resources');

  // Financial terms glossary for the Learning Center tab
  const financialTerms = [
    { term: "Asset Allocation", definition: "The process of dividing investments among different kinds of assets to optimize the risk/reward ratio." },
    { term: "Bear Market", definition: "A market condition in which prices are falling or expected to fall, typically accompanied by widespread pessimism." },
    { term: "Bull Market", definition: "A market condition in which prices are rising or expected to rise, encouraging buying." },
    { term: "Diversification", definition: "The practice of spreading money among different investments to reduce risk." },
    { term: "ETF (Exchange-Traded Fund)", definition: "A marketable security that tracks an index, commodities, bonds, or a basket of assets." },
    { term: "Hedge", definition: "An investment position intended to offset potential losses from other investments." },
    { term: "Liquidity", definition: "The degree to which an asset can be quickly bought or sold without affecting its price." },
    { term: "Market Capitalization", definition: "The total dollar market value of a company's outstanding shares." }
  ];

  // Finance industry timeline for the Career Portal tab
  const careerTimeline = [
    { year: "January", event: "Investment Banking Recruitment", description: "Major banks open summer analyst applications for juniors" },
    { year: "February", event: "Buy-Side Applications", description: "Hedge funds and asset management firms begin recruiting" },
    { year: "March", event: "Spring Internships", description: "Spring break externships and shadowing opportunities" },
    { year: "April", event: "Finance Conferences", description: "Industry conferences and networking events" },
    { year: "September", event: "Fall Recruitment", description: "Full-time positions open for graduating seniors" },
    { year: "October", event: "Financial Services Expo", description: "Major employers visit campus for recruiting events" },
    { year: "November", event: "Sophomore Programs", description: "Early identification programs for sophomores" }
  ];

  return (
    <section className="members">
      <div className="members-hero">
        <h1>Member Resources</h1>
        <div className="subtitle-line"></div>
        <p>Access exclusive tools and professional resources to accelerate your finance career</p>
      </div>

      <div className="members-tabs-container">
        <div className="members-tabs">
          <button 
            className={`tab-button ${activeTab === 'resources' ? 'active' : ''}`}
            onClick={() => setActiveTab('resources')}
          >
            <span className="icon resources-icon"></span>
            Resources
          </button>
          <button 
            className={`tab-button ${activeTab === 'learning' ? 'active' : ''}`}
            onClick={() => setActiveTab('learning')}
          >
            <span className="icon learning-icon"></span>
            Learning Center
          </button>
          <button 
            className={`tab-button ${activeTab === 'networking' ? 'active' : ''}`}
            onClick={() => setActiveTab('networking')}
          >
            <span className="icon networking-icon"></span>
            Networking
          </button>
          <button 
            className={`tab-button ${activeTab === 'careers' ? 'active' : ''}`}
            onClick={() => setActiveTab('careers')}
          >
            <span className="icon careers-icon"></span>
            Career Portal
          </button>
        </div>
      </div>

      <div className="tab-content">
        {/* Resources Tab Content */}
        {activeTab === 'resources' && (
          <div className="resources-container">
            <div className="featured-resource">
              <div className="featured-resource-content">
                <span className="tag">FEATURED</span>
                <h2>Bloomberg Terminal Access</h2>
                <p>Gain hands-on experience with industry-standard financial software. The Finance Club provides members with scheduled access to Bloomberg Terminals in the Business School lab.</p>
                <ul className="resource-details">
                  <li><span className="detail-icon location"></span>Business School Lab, Room 302</li>
                  <li><span className="detail-icon time"></span>Available Monday-Friday, 9am-6pm</li>
                  <li><span className="detail-icon info"></span>Reservation required</li>
                </ul>
                <a 
                  href="https://myaulife.adelphi.edu/organization/financeclub/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="resource-button"
                >
                  Access Resource
                  <span className="button-arrow">→</span>
                </a>
              </div>
              <div className="featured-resource-image">
  <img src="/assets/bllomberg.jpg" alt="Bloomberg Terminal" />
</div>

            </div>

            <h3 className="section-title">Essential Resources</h3>
            
            <div className="resources-grid">
              <div className="resource-card">
                <div className="resource-icon finance-club"></div>
                <h3>Finance Club Portal</h3>
                <p>View organization details, upcoming events, and member-only announcements.</p>
                <div className="resource-meta">
                  <span className="meta-info">Updated Weekly</span>
                </div>
                <a 
                  href="https://myaulife.adelphi.edu/organization/financeclub/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="card-link"
                >
                  <span>Access Portal</span>
                  <span className="arrow">→</span>
                </a>
              </div>
              
              <div className="resource-card">
                <div className="resource-icon member-services"></div>
                <h3>Member Services</h3>
                <p>Access exclusive member benefits, certificate programs, and development opportunities.</p>
                <div className="resource-meta">
                  <span className="meta-info">Members Only</span>
                </div>
                <a 
                  href="https://portal.adelphi.edu/services/redirect.php?service_id=122" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="card-link"
                >
                  <span>Access Services</span>
                  <span className="arrow">→</span>
                </a>
              </div>
              
              <div className="resource-card">
                <div className="resource-icon financial-data"></div>
                <h3>Financial Data Repository</h3>
                <p>Access our curated collection of financial datasets, market reports, and analytical tools.</p>
                <div className="resource-meta">
                  <span className="meta-info">Updated Daily</span>
                </div>
                <a 
                  href="https://myaulife.adelphi.edu/organization/financeclub/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="card-link"
                >
                  <span>Access Data</span>
                  <span className="arrow">→</span>
                </a>
              </div>
              
              <div className="resource-card">
                <div className="resource-icon research"></div>
                <h3>Research Publications</h3>
                <p>Browse finance club research papers, market analyses, and industry-specific insights.</p>
                <div className="resource-meta">
                  <span className="meta-info">25+ Publications</span>
                </div>
                <a 
                  href="https://myaulife.adelphi.edu/organization/financeclub/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="card-link"
                >
                  <span>View Library</span>
                  <span className="arrow">→</span>
                </a>
              </div>
            </div>
          </div>
        )}

        {/* Learning Center Tab Content */}
        {activeTab === 'learning' && (
          <div className="learning-container">
            <h3 className="section-title">Finance Terminology Glossary</h3>
            <p className="section-description">Essential financial terms every finance professional should know</p>

            <div className="glossary-container">
              {financialTerms.map((item, index) => (
                <div className="glossary-item" key={index}>
                  <h4>{item.term}</h4>
                  <p>{item.definition}</p>
                </div>
              ))}
            </div>

            <div className="learning-resources">
              <h3 className="section-title">Recommended Reading</h3>
              <div className="book-list">
                <div className="book-item">
                  <div className="book-cover" style={{backgroundColor: '#e9f2ff'}}></div>
                  <div className="book-info">
                    <h4>"The Intelligent Investor"</h4>
                    <p className="book-author">Benjamin Graham</p>
                    <p className="book-description">The definitive book on value investing, updated with commentary by Jason Zweig.</p>
                  </div>
                </div>

                <div className="book-item">
                  <div className="book-cover" style={{backgroundColor: '#fff0e5'}}></div>
                  <div className="book-info">
                    <h4>"A Random Walk Down Wall Street"</h4>
                    <p className="book-author">Burton G. Malkiel</p>
                    <p className="book-description">A time-tested guide to stock market investment strategies.</p>
                  </div>
                </div>

                <div className="book-item">
                  <div className="book-cover" style={{backgroundColor: '#f0f5e9'}}></div>
                  <div className="book-info">
                    <h4>"Principles: Life and Work"</h4>
                    <p className="book-author">Ray Dalio</p>
                    <p className="book-description">Investment principles from the founder of one of the world's largest hedge funds.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Networking Tab Content */}
        {activeTab === 'networking' && (
          <div className="networking-container">
            <h3 className="section-title">Networking Tips for Finance Professionals</h3>
            
            <div className="tips-container">
              <div className="tip-card">
                <div className="tip-number">01</div>
                <h4>Perfect Your Elevator Pitch</h4>
                <p>Craft a concise 30-second introduction that highlights your background, skills, and career goals in finance.</p>
              </div>
              
              <div className="tip-card">
                <div className="tip-number">02</div>
                <h4>Leverage LinkedIn</h4>
                <p>Optimize your LinkedIn profile with industry keywords and connect with finance professionals from target companies.</p>
              </div>
              
              <div className="tip-card">
                <div className="tip-number">03</div>
                <h4>Attend Industry Conferences</h4>
                <p>Participate in finance conferences and seminars to meet professionals and stay current with industry trends.</p>
              </div>
              
              <div className="tip-card">
                <div className="tip-number">04</div>
                <h4>Join Professional Associations</h4>
                <p>Become a member of finance organizations like CFA Society or Financial Planning Association.</p>
              </div>
              
              <div className="tip-card">
                <div className="tip-number">05</div>
                <h4>Follow Up Consistently</h4>
                <p>Send personalized follow-up messages within 24-48 hours after making new connections.</p>
              </div>
              
              <div className="tip-card">
                <div className="tip-number">06</div>
                <h4>Offer Value First</h4>
                <p>Share relevant articles, insights, or introductions before asking for favors from your network.</p>
              </div>
            </div>

            <div className="networking-events">
              <h3 className="section-title">Annual Finance Networking Events</h3>
              <div className="events-timeline">
                <div className="timeline-item">
                  <div className="timeline-date">January</div>
                  <div className="timeline-content">
                    <h4>Wall Street Conference</h4>
                    <p>New York City</p>
                  </div>
                </div>
                <div className="timeline-item">
                  <div className="timeline-date">March</div>
                  <div className="timeline-content">
                    <h4>Financial Innovation Forum</h4>
                    <p>Boston</p>
                  </div>
                </div>
                <div className="timeline-item">
                  <div className="timeline-date">May</div>
                  <div className="timeline-content">
                    <h4>Investment Management Symposium</h4>
                    <p>Chicago</p>
                  </div>
                </div>
                <div className="timeline-item">
                  <div className="timeline-date">September</div>
                  <div className="timeline-content">
                    <h4>Global Finance Summit</h4>
                    <p>London</p>
                  </div>
                </div>
                <div className="timeline-item">
                  <div className="timeline-date">November</div>
                  <div className="timeline-content">
                    <h4>Finance Leaders Forum</h4>
                    <p>San Francisco</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Career Portal Tab Content */}
        {activeTab === 'careers' && (
          <div className="careers-container">
            <h3 className="section-title">Finance Recruitment Timeline</h3>
            <p className="section-description">Key recruitment periods for finance positions throughout the year</p>

            <div className="career-timeline">
              {careerTimeline.map((item, index) => (
                <div className="timeline-card" key={index}>
                  <div className="timeline-month">{item.year}</div>
                  <div className="timeline-content">
                    <h4>{item.event}</h4>
                    <p>{item.description}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="career-paths">
              <h3 className="section-title">Finance Career Paths</h3>
              
              <div className="paths-grid">
                <div className="path-card">
                  <div className="path-icon investment-banking"></div>
                  <h4>Investment Banking</h4>
                  <ul className="path-details">
                    <li>Analyst → Associate → VP → Director → Managing Director</li>
                    <li>Starting Salary: $85,000-$100,000 + Bonus</li>
                    <li>Focus: M&A, Capital Raising, Financial Advisory</li>
                  </ul>
                </div>
                
                <div className="path-card">
                  <div className="path-icon asset-management"></div>
                  <h4>Asset Management</h4>
                  <ul className="path-details">
                    <li>Analyst → Portfolio Manager → Fund Manager</li>
                    <li>Starting Salary: $70,000-$90,000 + Bonus</li>
                    <li>Focus: Investment Analysis, Portfolio Construction</li>
                  </ul>
                </div>
                
                <div className="path-card">
                  <div className="path-icon corporate-finance"></div>
                  <h4>Corporate Finance</h4>
                  <ul className="path-details">
                    <li>Financial Analyst → Manager → Controller → CFO</li>
                    <li>Starting Salary: $60,000-$80,000</li>
                    <li>Focus: Financial Planning, Strategy, Reporting</li>
                  </ul>
                </div>
                
                <div className="path-card">
                  <div className="path-icon risk-management"></div>
                  <h4>Risk Management</h4>
                  <ul className="path-details">
                    <li>Analyst → Risk Manager → Chief Risk Officer</li>
                    <li>Starting Salary: $65,000-$85,000</li>
                    <li>Focus: Credit, Market, Operational Risk</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="cta-banner">
        <div className="cta-content">
          <h3>Join the Finance Club Today</h3>
          <p>Get unlimited access to all resources, events, and networking opportunities</p>
        </div>
        <a 
          href="https://myaulife.adelphi.edu/organization/financeclub/" 
          target="_blank" 
          rel="noopener noreferrer"
          className="cta-button"
        >
          Become a Member
        </a>
      </div>
    </section>
  );
};

export default Members;